import React, { useState, useRef, useEffect } from 'react';

export function SplitButton({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md', 
  disabled = false, 
  icon = null, 
  className = '',
  dropdownItems = []
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const baseClasses = 'relative inline-flex items-center font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 transform active:scale-95 overflow-hidden';
  
  const variants = {
    primary: 'bg-linear-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl focus:ring-blue-500',
    secondary: 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-xs hover:shadow-md focus:ring-slate-500',
    success: 'bg-linear-to-r from-emerald-600 to-green-600 text-white hover:from-emerald-700 hover:to-green-700 shadow-lg hover:shadow-xl focus:ring-emerald-500',
    warning: 'bg-linear-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl focus:ring-orange-500',
    ghost: 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:ring-slate-500'
  };
  
  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };
  
  const disabledClasses = 'opacity-60 cursor-not-allowed transform-none bg-linear-to-r from-slate-400 to-slate-500 text-slate-100 dark:from-slate-600 dark:to-slate-700 dark:text-slate-300 shadow-xs';
  
  const buttonClasses = `
    ${baseClasses} 
    ${disabled ? disabledClasses : variants[variant]} 
    ${sizes[size]} 
    ${className}
  `;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMainClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleItemClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  return (
    <div className="relative inline-flex" ref={dropdownRef}>
      {/* Main Button */}
      <button
        onClick={handleMainClick}
        disabled={disabled}
        className={`${buttonClasses} border-r-0`}
      >
        {!disabled && (
          <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/10 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 transform -skew-x-12 -translate-x-full hover:translate-x-full"></div>
        )}
        
        <div className="relative flex items-center space-x-2">
          {icon && <div className="w-5 h-5">{icon}</div>}
          <span>{children}</span>
        </div>
      </button>

      {/* Dropdown Arrow Button */}
      <button
        onClick={handleDropdownClick}
        disabled={disabled}
        className={`${buttonClasses} border-l-0 px-2`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && dropdownItems.length > 0 && (
        <div className="absolute top-full right-0 mt-1 w-48 bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 z-50">
          <div className="py-1">
            {dropdownItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleItemClick(item)}
                disabled={item.disabled}
                className={`w-full px-4 py-2 text-left text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center space-x-3 ${
                  item.disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {item.icon && <div className="w-4 h-4 text-slate-500 dark:text-slate-400">{item.icon}</div>}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
